import { Col, Modal, Row, Button } from "@mtm/components";
import notification from "antd/lib/notification";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { DeleteServiceUser } from "../../../services/ProfilingService";
import {
  RegistrationEnums,
  WandaTripStatus,
} from "../../../types/ApplicationTypes";
import EditIcon from "../../../assets/images/blueCircleEditIcon.svg";
import DeleteIcon from "../../../assets/images/blueCircleDeleteIcon.svg";
import {
  DefaultDeleteUserData,
  DeleteUserData,
} from "../../../types/ProfileTypes";
import { IServiceUser } from "../Interfaces/IServiceUser";
import { getFormattedDate } from "../Utils";
import serviceUsersImage from "../../../assets/images/avatar.png";
import { deleteUserTrashIcon, editProfileIcon } from "../../../assets/images";
import styles from "./ProfileComponent.module.scss";
import EllipsisText from "../../../helpers/EllipsisText/EllipsisText";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import CustomTooltip from "../../../common/Tooltips/CustomTooltip";
import indicationIcon from "../../../assets/images/indicationIcon.svg";
import wheelChairIcon from "../../../assets/images/wheelChairIcon.svg";
import moment from "moment";
import { getWandaTripWithPagination } from "../../../services/RegistrationService";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import { useDashboardCtx } from "../../../context/DashboardContext";

interface Props {
  user: IServiceUser;
}

const ProfileComponent: React.FC<Props> = ({ user }) => {
  const [deletePopUp, setDeletePopUp] = useState<boolean>(false);
  const [removePopUp, setRemovePopUp] = useState<boolean>(false);
  const [deleteUserInfo, setDeleteUserInfo] = useState<DeleteUserData>(
    DefaultDeleteUserData
  );
  const [loadDataProgress, setLoadDataProgress] = useState(false);

  const { selectedPersonIdCtx } = useDashboardCtx();
  const [, setSelectedPersonId] = selectedPersonIdCtx;

  const { serviceDataCtx, wandaOrgId } = useTripsCtx();
  const [serviceData, setServiceData] = serviceDataCtx;

  const DEFAULT_CLASS_NAME = `profile_container`;
  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const history = useHistory();

  async function editHandler(id: Number) {
    history.push(`/DashBoard/EditServiceProfile/${id}`);
  }

  async function handleDeleteServiceUser(record: IServiceUser) {
    const data = {
      userName: record.firstName + " " + record.lastName,
      personId: record.personId,
    };
    setDeleteUserInfo(data);
    // needs to check person have already Trips or not
    const todaysDate = String(moment().format("MM/DD/YYYY"));
    const futureDate = String(
      moment()
        .add(12, "months")
        .format("MM/DD/YYYY")
    );
    let trips = await getWandaTripWithPagination({
      personId: record.personId,
      wandaTripStatusIds: WandaTripStatus.Booked,
      skipPagination: true,
      fromDate: todaysDate,
      toDate: futureDate,
      selectedPersonId: String(record.personId),
    });
    if (trips.totalTripCount > 0) {
      setRemovePopUp(true);
    } else {
      setDeletePopUp(true);
    }
  }
  async function handleClosePopupSuccess() {
    setRemovePopUp(false);
  }

  async function handleDeletePopupSuccess() {
    const data = {
      organizationId: wandaOrgId.benefitGroupId,
      personId: deleteUserInfo.personId,
      personTypeId: RegistrationEnums.PersonTypeId,
      isActive: false,
      relationshipRefID: RegistrationEnums.PersonToBenefitRel,
    };
    try {
      setLoadDataProgress(true);
      let result = await DeleteServiceUser(data, caregiverSummaryInfo.personId);
      if (result.data.success === true) {
        setLoadDataProgress(false);
        const filteredServiceData = serviceData.filter(
          (serviceUser: { personId: number }) =>
            serviceUser.personId !== data.personId
        );
        setServiceData(filteredServiceData);
        setSelectedPersonId(
          filteredServiceData.length === 1
            ? filteredServiceData[0]?.personId
            : ""
        );
        history.push(`/DashBoard`);
      }
      setDeletePopUp(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to delete service user. Please try again later.",
      });
    }
  }
  function handleDeletePopupCancel() {
    setDeletePopUp(false);
  }

  const content = (
    <Row justify="center">
      <Col span={24}>
        {user.wandaAccommodations?.wheelChairPresent
          ? "Standard, foldable wheelchair"
          : "Non-foldable wheelchair"}
      </Col>

      <Col>
        {user.wandaAccommodations?.wcToVehicleTransfer
          ? "Able to transfer"
          : "Not able to transfer"}
      </Col>
    </Row>
  );

  return (
    <>
      <div className={DEFAULT_CLASS_NAME}>
        <Row justify="center" gutter={[0, 10]}>
          <Col>
            <div>
              {!loadDataProgress && (
                <div>
                  {user.profilePicURL !== "" ? (
                    <img
                      className={styles.profilePic}
                      src={user.profilePicURL}
                      alt="avtar"
                    />
                  ) : (
                    <img
                      className={styles.profilePic}
                      src={serviceUsersImage}
                      alt="serviceUsersImage"
                    />
                  )}
                </div>
              )}
            </div>
          </Col>
          <Col span={24} />
        </Row>
        <Row justify="center" gutter={[0, 6]}>
          <Col className={styles.serviceUserName}>
            <EllipsisText
              text={`${user.firstName} ${user.lastName} `}
              maxVisibleTextLength={20}
            />
          </Col>
          <Col span={24} />
        </Row>

        <Row>
          <Col xs={0} lg={24}>
            <Row justify="center" gutter={[10, 29]}>
              <Col>
                <Button
                  clickHandler={() => editHandler(user.personId)}
                  className={styles.profileEditBtn}
                >
                  <img src={EditIcon} alt="editServiceUser" />
                </Button>
              </Col>
              <Col>
                <Button
                  clickHandler={() => handleDeleteServiceUser(user)}
                  className={styles.profileDeleteBtn}
                >
                  <img src={DeleteIcon} alt="deleteServiceUser" />
                </Button>
              </Col>
              <Col span={24} />
            </Row>
          </Col>
          <Col xs={24} lg={0}>
            <Row
              justify="center"
              className={styles.profileMobileBtnControls}
              gutter={[10, 0]}
            >
              <Col>
                <Button
                  clickHandler={() => editHandler(user.personId)}
                  className={styles.editBtn}
                >
                  <img src={editProfileIcon} alt="editServiceUser" />
                  Edit Profile
                </Button>
              </Col>
              <Col>
                <Button
                  clickHandler={() => handleDeleteServiceUser(user)}
                  className={styles.trashBtn}
                >
                  <img src={deleteUserTrashIcon} alt="deleteServiceUser" />
                  Delete User
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row justify="center" gutter={[0, 15]}>
          {getFormattedDate(user.dateOfBirth, "YYYY-MM-DD") !==
            RegistrationEnums.DateOfBirth && (
            <Col span={24}>
              <Row justify="center" gutter={[22, 0]}>
                <Col span={24} className={styles.profileDetailsHeading}>
                  Date of Birth
                </Col>
                <Col className={styles.profileDetailsText}>
                  {`${getFormattedDate(user.dateOfBirth, "MM/DD/YYYY")}`}
                </Col>
                <Col
                  className={styles.profileDetailsSubText}
                >{`${user.age} years old`}</Col>
              </Row>
            </Col>
          )}

          <Col xs={12} sm={12} lg={24}>
            <Col span={24} className={styles.profileDetailsHeading}>
              Phone Number
            </Col>
            <Col className={styles.profileDetailsText}>{user.phoneNumber}</Col>
          </Col>

          {user.email && user.email !== "" && (
            <Col xs={12} sm={12} lg={24}>
              <Col span={24} className={styles.profileDetailsHeading}>
                Email Address
              </Col>
              <Col className={styles.profileDetailsText}>
                <EllipsisText text={user.email} maxVisibleTextLength={30} />
              </Col>
            </Col>
          )}

          <Col xs={12} sm={12} lg={24}>
            <Col span={24} className={styles.profileDetailsHeading}>
              Home Address
            </Col>
            <Col className={styles.profileDetailsText}>{user.homeAddress}</Col>
          </Col>

          <Col xs={12} sm={12} lg={24}>
            <Col span={24} className={styles.profileDetailsHeading}>
              Mailing Address
            </Col>
            <Col className={styles.profileDetailsText}>
              {user.mailingAddress}
            </Col>
          </Col>

          {user.wandaAccommodations?.wheelChairTravel === true && (
            <Col span={24}>
              <Col span={24} className={styles.profileDetailsHeading}>
                Accommodations
              </Col>
              <Col span={24} className={styles.profileDetailsText}>
                <Row justify="center" align="middle" gutter={[3, 0]}>
                  <Col>
                    <img src={wheelChairIcon} alt="wheelChair" />
                  </Col>
                  <Col>Travels with wheelchair</Col>
                  <Col>
                    <CustomTooltip
                      tooltipText={content}
                      className={`customTooltip  ${styles.tooltipStyle}`}
                      icon={indicationIcon}
                      trigger={["hover", "click"]}
                      placement={"top"}
                    />
                  </Col>
                </Row>
              </Col>
            </Col>
          )}

          <Col xs={24} lg={0}>
            <Row gutter={[0, 25]} justify="center">
              <Col span={24} />
              <Col span={24} className={styles.dashedLine} />
            </Row>
          </Col>
        </Row>

        <Modal
          handleSuccess={handleDeletePopupSuccess}
          visible={deletePopUp}
          okButtonProps={{ hidden: true }}
          cancelButtonProps={{ hidden: true }}
          centered={true}
          className={`modalDeleteWidth`}
        >
          <div className={`modalDeleteHeading`}>Remove Service User</div>
          <div className={`modalDeleteText` + " subTextFont "}>
            Are you sure you want to remove <b>{deleteUserInfo.userName}</b>{" "}
            from your Service Users?
          </div>
          <div className={`modalDeleteBtns`}>
            <Button
              key="close"
              className={`modalDeleteBtnCancel`}
              clickHandler={handleDeletePopupCancel}
            >
              Cancel
            </Button>
            <Button
              key="close"
              className={`modalDeleteBtnSave`}
              clickHandler={handleDeletePopupSuccess}
            >
              Yes, Remove
            </Button>
          </div>
        </Modal>

        <Modal
          handleSuccess={handleDeletePopupSuccess}
          visible={removePopUp}
          okButtonProps={{ hidden: true }}
          cancelButtonProps={{ hidden: true }}
          centered={true}
          className={`modalDeleteWidth`}
        >
          <div className={`modalDeleteHeading`}>Can't Remove Service User</div>
          <div className={`modalDeleteText subTextFont `}>
            <b>{deleteUserInfo.userName}</b> has scheduled Upcoming Services and
            can't be removed at this time.
          </div>
          <div className={`modalDeleteText subTextFont `}>
            To remove this service user, please cancel all of their Upcoming
            Services and then try again.
          </div>
          <div className={`modalDeleteBtns`}>
            <Button
              key="close"
              className={`modalDeleteBtnSave`}
              clickHandler={handleClosePopupSuccess}
            >
              Close
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default ProfileComponent;
